import React, { useState } from "react";
import { Avatar, Button, Divider, Empty, Tag, Typography } from "antd";
import {
  CalendarOutlined,
  DownloadOutlined,
  ExportOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  ProfileMatchStatus,
  QuestionnaireStatus,
} from "../../../Entities/JobApplication";
import dayjs from "dayjs";
import { User } from "../../../Entities/User";
import { JobApplication } from "../../../Entities/JobApplication";
import { UserProfile } from "../../../Entities/UserProfile";
import { openNotification } from "../../../Components/Notification";
import { useNavigate, useSearchParams } from "react-router-dom";
import CandidateResumePreview from "../../../Components/CandidateResumePreview";

interface CandidateBasicDetailsProps {
  candidate: User;
  recentApplication: JobApplication | null;
  recentResume: UserProfile | null;
}

const CandidateBasicDetails: React.FC<CandidateBasicDetailsProps> = ({
  candidate,
  recentApplication,
  recentResume,
}) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { firstName, lastName, email, mobileNumber, createdAt } = candidate;
  const [isOpen, setIsOpen] = useState(false);

  const capitalize = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((n) => n![0].toUpperCase())
    .join("");

  const applicationStatusColor: Record<string, string> = {
    applied: "blue",
    shortlisted: "gold",
    interview: "magenta",
    rejected: "red",
    accepted: "cyan",
    hired: "green",
  };

  const profileMatchTag = (pm?: ProfileMatchStatus) => {
    if (!pm || pm.status === "pending") return <Tag color="blue">Pending</Tag>;
    if (pm.status === "qualified") return <Tag color="green">Qualified</Tag>;
    return <Tag color="red">Unqualified</Tag>;
  };

  const questionnaireTag = (qs?: QuestionnaireStatus) => {
    const map: Record<QuestionnaireStatus, { color: string; label: string }> = {
      qualified: { color: "green", label: "Qualified" },
      unqualified: { color: "red", label: "Unqualified" },
      partially_qualified: { color: "orange", label: "Partial" },
      pending: { color: "blue", label: "Pending" },
      action_required: { color: "red", label: "Action Required" },
    };
    if (!qs) return <Tag color="blue">Pending</Tag>;
    const { color, label } = map[qs];
    return <Tag color={color}>{label}</Tag>;
  };

  const handleViewResume = (resume: UserProfile) => {
    setIsOpen(true);
  };

  const handleDownloadResume = async (resume: UserProfile) => {
    try {
      const url = `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/profile/${resume.userId}/${resume.filename}`;

      // Fetch the file as a blob to handle CORS properly
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resume.filename || "resume.pdf";
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading resume:", error);
      openNotification(
        "error",
        "Download Failed",
        "Failed to download the resume. Please try again.",
      );
    }
  };

  const handleViewApplicationDetails = (application: JobApplication) => {
    const applicationId = application._id.toString();
    const jobPostId = application.jobPost._id;
    const url = `/application-details?applicationId=${applicationId}&jobId=${jobPostId}`;
    navigate(url);
  };

  const handleViewMore = (key: "applications" | "resumes") => {
    setSearchParams({ tab: key }, { replace: true });
  };

  return (
    <div className="candidate-basic-details">
      <div className="candidate-basic-details__left">
        <div className="candidate-details-card candidate-profile-card">
          <div className="candidate-profile-card__header">
            <Avatar className="candidate-profile-card__avatar" size={72}>
              {initials}
            </Avatar>
            <Typography.Title
              level={5}
              style={{ margin: 0 }}
              className="candidate-profile-card__name"
            >
              {capitalize(firstName)} {capitalize(lastName)}
            </Typography.Title>
          </div>
          <div className="candidate-profile-card__info">
            <div className="candidate-profile-card__info-row">
              <Typography.Text
                type="secondary"
                className="candidate-profile-card__info-label"
              >
                Email
              </Typography.Text>
              <Typography.Text>
                <MailOutlined /> {email || "NA"}
              </Typography.Text>
            </div>
            <div className="candidate-profile-card__info-row">
              <Typography.Text
                type="secondary"
                className="candidate-profile-card__info-label"
              >
                Phone
              </Typography.Text>
              <Typography.Text>
                <PhoneOutlined rotate={90} /> {mobileNumber || "NA"}
              </Typography.Text>
            </div>
            <div className="candidate-profile-card__info-row">
              <Typography.Text
                type="secondary"
                className="candidate-profile-card__info-label"
              >
                Joined On
              </Typography.Text>
              <Typography.Text>
                <CalendarOutlined />{" "}
                {createdAt ? dayjs(createdAt).format("DD MMM YYYY") : "NA"}
              </Typography.Text>
            </div>
          </div>
        </div>
      </div>
      <div className="candidate-basic-details__right">
        <div className="candidate-details-card">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Recent Application
          </Typography.Title>
          <Divider style={{ margin: 0 }} />
          {!recentApplication && (
            <Empty
              style={{ margin: 0 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="There are no recent job application found"
            />
          )}
          {recentApplication && (
            <div className="recent-card recent-application-card">
              <div className="recent-application-card-header">
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {recentApplication.jobPost.title}
                </Typography.Title>
                <Tag color={applicationStatusColor[recentApplication.status]}>
                  {recentApplication.status}
                </Tag>
              </div>
              <div className="recent-application-meta">
                <div className="recent-application-meta__item">
                  <Typography.Text
                    type="secondary"
                    className="recent-application-meta__label"
                  >
                    <CalendarOutlined /> Applied On
                  </Typography.Text>
                  <Typography.Text>
                    {dayjs(recentApplication.applied_date).format(
                      "DD MMM YYYY",
                    )}
                  </Typography.Text>
                </div>
                <div className="recent-application-meta__item">
                  <Typography.Text
                    type="secondary"
                    className="recent-application-meta__label"
                  >
                    <UserOutlined /> Profile Match
                  </Typography.Text>
                  {profileMatchTag(recentApplication.profileMatchStatus)}
                </div>
                <div className="recent-application-meta__item">
                  <Typography.Text
                    type="secondary"
                    className="recent-application-meta__label"
                  >
                    <FileTextOutlined /> Assessment
                  </Typography.Text>
                  {questionnaireTag(recentApplication.questionnaireStatus)}
                </div>
                <div className="recent-application-meta__item">
                  <Typography.Text
                    type="secondary"
                    className="recent-application-meta__label"
                  >
                    <VideoCameraOutlined /> Interview
                  </Typography.Text>
                  <Typography.Text>
                    {recentApplication.interviewSchedule
                      ? dayjs(recentApplication.interviewSchedule).format(
                          "DD MMM YYYY",
                        )
                      : "NA"}
                  </Typography.Text>
                </div>
              </div>
              <div className="resume-actions">
                <Button
                  icon={<EyeOutlined />}
                  iconPosition="end"
                  onClick={() =>
                    handleViewApplicationDetails(recentApplication)
                  }
                >
                  View
                </Button>
              </div>
            </div>
          )}
          {recentApplication && <Divider style={{ margin: 0 }} />}
          {recentApplication && (
            <div className="recent-card-footer">
              <Typography.Text type="secondary">View more</Typography.Text>
              <Button
                onClick={() => handleViewMore("applications")}
                type="link"
                icon={<ExportOutlined />}
              />
            </div>
          )}
        </div>
        <div className="candidate-details-card">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Recently Uploaded Resume
          </Typography.Title>
          <Divider style={{ margin: 0 }} />
          {!recentResume && (
            <Empty
              style={{ margin: 0 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="There are no recently uploaded resume found"
            />
          )}
          {recentResume && (
            <div className="recent-card recent-resume-card">
              <div className="resume-left">
                <div className="resume-icon">
                  <FilePdfOutlined />
                </div>
                <div className="resumes-info">
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {recentResume.filename}
                  </Typography.Title>
                  <div className="resumes__meta">
                    <Typography.Text type="secondary">
                      Uploaded on :{" "}
                      {dayjs(recentResume.createdAt).format(
                        "DD MMM YYYY hh:mm a",
                      )}
                    </Typography.Text>
                  </div>
                </div>
              </div>
              <div className="resume-actions">
                <Button
                  onClick={() => handleViewResume(recentResume)}
                  icon={<EyeOutlined />}
                  iconPosition="end"
                >
                  View
                </Button>
                <Button
                  onClick={() => handleDownloadResume(recentResume)}
                  icon={<DownloadOutlined />}
                  iconPosition="end"
                >
                  Download
                </Button>
              </div>
            </div>
          )}
          {recentResume && <Divider style={{ margin: 0 }} />}
          {recentResume && (
            <div className="recent-card-footer">
              <Typography.Text type="secondary">View more</Typography.Text>
              <Button
                onClick={() => handleViewMore("resumes")}
                type="link"
                icon={<ExportOutlined />}
              />
            </div>
          )}
        </div>
      </div>
      {recentResume && (
        <CandidateResumePreview
          isOpen={isOpen}
          userProfile={recentResume}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CandidateBasicDetails;
